import { useEffect, useState } from "react";

// Shared across the app so deferred requests are only delayed on the first page load
let isReady = false;

const IDLE_TIMEOUT = 2000;
const FALLBACK_DELAY = 1000;

/**
 * Returns `true` once the browser is idle after the initial render.
 * Used to postpone non-critical requests so they don't fire in the same burst
 * as the main page data.
 */
export const useDeferredFetch = (): boolean => {
  const [ready, setReady] = useState<boolean>(isReady);

  useEffect(() => {
    if (ready) return;

    const markReady = () => {
      isReady = true;
      setReady(true);
    };

    // Safari doesn't support requestIdleCallback
    if ("requestIdleCallback" in window) {
      const id = window.requestIdleCallback(markReady, {
        timeout: IDLE_TIMEOUT,
      });
      return () => window.cancelIdleCallback(id);
    }

    const id = setTimeout(markReady, FALLBACK_DELAY);
    return () => clearTimeout(id);
  }, [ready]);

  return ready;
};
